import { c as _c } from "react/compiler-runtime";
import { patchHostedUserVaultMutation } from '@onefootprint/fetch-hosted';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { Locale, VerifiedStepUpKind } from '@onefootprint/request-types';
import { BottomSheet, Button, Divider, Form } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IDV_BODY_CONTENT_CONTAINER_ID, LEGACY_IDV_CONTAINER_ID } from '@/idv2/components/constants';
import { PoweredByVerified } from '@/idv2/components/powered-by-verified';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { getLogger } from '@/idv2/utils/logger';
import { suppressNextStuckDetection } from '@/idv2/utils/route-tracking';
const {
  logInfo,
  logError
} = getLogger({
  name: 'idv2',
  location: 'verified-prefill-overlay'
});
type FormData = {
  value: string;
};
const DI_BY_KIND: Record<VerifiedStepUpKind, string> = {
  dob: 'id.dob',
  ssn4: 'id.ssn4',
  first_name: 'id.first_name'
};

// dob (10 chars) and ssn4 (4 digits) have a deterministic length, so the overlay auto-submits the
// moment the field is filled. A first name has no such signal, so it submits explicitly instead.
const AUTO_SUBMIT_KINDS: VerifiedStepUpKind[] = ['dob', 'ssn4'];
const isComplete = (kind: VerifiedStepUpKind, value: string | undefined): boolean => {
  if (!value) return false;
  switch (kind) {
    case 'dob':
      return value.length === 10;
    case 'ssn4':
      return /^\d{4}$/.test(value);
    default:
      return false;
  }
};
type VerifiedPrefillOverlayContentProps = {
  locale?: Locale;
  stepUpKind: VerifiedStepUpKind;
  isPending: boolean;
  onSubmit: (value: string) => void;
};

// The field + copy + submit affordances, without the BottomSheet/vault/navigation wiring — so it
// snapshots in a Storybook Gallery without a router or a portal target.
export const VerifiedPrefillOverlayContent = t0 => {
  const $ = _c(69);
  const {
    locale,
    stepUpKind,
    isPending,
    onSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectData.pages.verifiedPrefillOverlay"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mode: "onChange"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const methods = useForm(t2);
  let t3;
  if ($[2] !== methods.control) {
    t3 = {
      control: methods.control,
      name: "value"
    };
    $[2] = methods.control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const value = useWatch(t3);
  let t4;
  if ($[4] !== methods.formState.isValid || $[5] !== onSubmit || $[6] !== stepUpKind || $[7] !== value) {
    t4 = () => {
      if (methods.formState.isValid && AUTO_SUBMIT_KINDS.includes(stepUpKind) && isComplete(stepUpKind, value)) {
        onSubmit(value);
      }
    };
    $[4] = methods.formState.isValid;
    $[5] = onSubmit;
    $[6] = stepUpKind;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== methods.formState.isValid || $[10] !== value) {
    t5 = [value, methods.formState.isValid];
    $[9] = methods.formState.isValid;
    $[10] = value;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== isPending || $[13] !== locale || $[14] !== stepUpKind || $[15] !== t) {
    t6 = () => {
      switch (stepUpKind) {
        case "dob":
          {
            return <Form.Dob name="value" data-nid-target="dob" data-dd-privacy="mask" label={t(_temp)} locale={locale} disabled={isPending} />;
          }
        case "ssn4":
          {
            return <Form.Ssn4 name="value" data-nid-target="ssn4" data-dd-privacy="mask" label={t(_temp2)} disabled={isPending} />;
          }
        case "first_name":
          {
            return <Form.Input name="value" data-nid-target="first-name" data-dd-privacy="mask" label={t(_temp3)} disabled={isPending} rules={{
              required: true
            }} />;
          }
        default:
          {
            return null;
          }
      }
    };
    $[12] = isPending;
    $[13] = locale;
    $[14] = stepUpKind;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const renderField = t6;
  let t7;
  bb0: switch (stepUpKind) {
    case "dob":
      {
        let t8;
        if ($[17] !== t) {
          t8 = t(_temp4);
          $[17] = t;
          $[18] = t8;
        } else {
          t8 = $[18];
        }
        let t9;
        if ($[19] !== t) {
          t9 = t(_temp5);
          $[19] = t;
          $[20] = t9;
        } else {
          t9 = $[20];
        }
        let t10;
        if ($[21] !== t8 || $[22] !== t9) {
          t10 = {
            title: t8,
            subtitle: t9
          };
          $[21] = t8;
          $[22] = t9;
          $[23] = t10;
        } else {
          t10 = $[23];
        }
        t7 = t10;
        break bb0;
      }
    case "ssn4":
      {
        let t8;
        if ($[24] !== t) {
          t8 = t(_temp6);
          $[24] = t;
          $[25] = t8;
        } else {
          t8 = $[25];
        }
        let t9;
        if ($[26] !== t) {
          t9 = t(_temp7);
          $[26] = t;
          $[27] = t9;
        } else {
          t9 = $[27];
        }
        let t10;
        if ($[28] !== t8 || $[29] !== t9) {
          t10 = {
            title: t8,
            subtitle: t9
          };
          $[28] = t8;
          $[29] = t9;
          $[30] = t10;
        } else {
          t10 = $[30];
        }
        t7 = t10;
        break bb0;
      }
    case "first_name":
      {
        let t8;
        if ($[31] !== t) {
          t8 = t(_temp8);
          $[31] = t;
          $[32] = t8;
        } else {
          t8 = $[32];
        }
        let t9;
        if ($[33] !== t) {
          t9 = t(_temp9);
          $[33] = t;
          $[34] = t9;
        } else {
          t9 = $[34];
        }
        let t10;
        if ($[35] !== t8 || $[36] !== t9) {
          t10 = {
            title: t8,
            subtitle: t9
          };
          $[35] = t8;
          $[36] = t9;
          $[37] = t10;
        } else {
          t10 = $[37];
        }
        t7 = t10;
        break bb0;
      }
    default:
      {
        let t8;
        if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = {
            title: "",
            subtitle: ""
          };
          $[38] = t8;
        } else {
          t8 = $[38];
        }
        t7 = t8;
      }
  }
  const copy = t7;
  let t8;
  if ($[39] !== copy.title) {
    t8 = <p className="text-heading-3 text-primary">{copy.title}</p>;
    $[39] = copy.title;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== copy.subtitle) {
    t9 = <p className="text-body-2 text-secondary flex items-center gap-1 justify-center">{copy.subtitle}</p>;
    $[41] = copy.subtitle;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== t8 || $[44] !== t9) {
    t10 = <div className="flex flex-col gap-2 text-center">{t8}{t9}</div>;
    $[43] = t8;
    $[44] = t9;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  let t11;
  if ($[46] !== methods || $[47] !== onSubmit) {
    let t12;
    if ($[49] !== onSubmit) {
      t12 = t13 => {
        const {
          value: submitted
        } = t13;
        return onSubmit(submitted);
      };
      $[49] = onSubmit;
      $[50] = t12;
    } else {
      t12 = $[50];
    }
    t11 = methods.handleSubmit(t12);
    $[46] = methods;
    $[47] = onSubmit;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[51] !== renderField) {
    t12 = renderField();
    $[51] = renderField;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== isPending || $[54] !== stepUpKind || $[55] !== t) {
    t13 = stepUpKind === "first_name" && <Button type="submit" variant="primary" fullWidth={true} size="large" loading={isPending}>{t(_temp10)}</Button>;
    $[53] = isPending;
    $[54] = stepUpKind;
    $[55] = t;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  let t14;
  if ($[57] !== t11 || $[58] !== t12 || $[59] !== t13) {
    t14 = <form className="flex flex-col gap-4" onSubmit={t11}>{t12}{t13}</form>;
    $[57] = t11;
    $[58] = t12;
    $[59] = t13;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  let t15;
  if ($[61] !== methods || $[62] !== t14) {
    t15 = <FormProvider {...methods}>{t14}</FormProvider>;
    $[61] = methods;
    $[62] = t14;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  let t17;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Divider />;
    t17 = <PoweredByVerified />;
    $[64] = t16;
    $[65] = t17;
  } else {
    t16 = $[64];
    t17 = $[65];
  }
  let t18;
  if ($[66] !== t10 || $[67] !== t15) {
    t18 = <div className="flex flex-col gap-6 px-5 pb-3">{t10}{t15}{t16}{t17}</div>;
    $[66] = t10;
    $[67] = t15;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  return t18;
};
type VerifiedPrefillOverlayProps = {
  authToken: string;
  locale?: Locale;
  stepUpKind: VerifiedStepUpKind;
};
export const VerifiedPrefillOverlay = t0 => {
  const $ = _c(20);
  const {
    locale,
    authToken,
    stepUpKind
  } = t0;
  const [isExpanded, setIsExpanded] = useState(false);
  const footprint = useFootprint();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = patchHostedUserVaultMutation();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const vaultMutation = useMutation(t1);
  const showRequestErrorToast = useRequestErrorToast();
  const scrollTo = _temp11;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsExpanded(true);
      scrollTo("bottom");
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== authToken || $[4] !== footprint || $[5] !== showRequestErrorToast || $[6] !== stepUpKind || $[7] !== vaultMutation) {
    t4 = async submittedValue => {
      if (vaultMutation.isPending) {
        return;
      }
      ;
      try {
        const body = {
          [DI_BY_KIND[stepUpKind]]: submittedValue
        };
        logInfo("Verified prefill overlay vault mutation", {
          vaultKeys: Object.keys(body).join(",")
        });
        await vaultMutation.mutateAsync({
          body,
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        suppressNextStuckDetection();
        await footprint.navigation.forward();
      } catch (t5) {
        const error = t5;
        showRequestErrorToast(error);
        logError("Verified prefill overlay error", error);
      }
    };
    $[3] = authToken;
    $[4] = footprint;
    $[5] = showRequestErrorToast;
    $[6] = stepUpKind;
    $[7] = vaultMutation;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const submitValue = t4;
  let t5;
  if ($[9] !== vaultMutation.isPending) {
    t5 = () => {
      if (!vaultMutation.isPending) {
        scrollTo("top");
        setIsExpanded(false);
      }
    };
    $[9] = vaultMutation.isPending;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleClose = t5;
  let t6;
  if ($[11] !== locale || $[12] !== stepUpKind || $[13] !== submitValue || $[14] !== vaultMutation.isPending) {
    t6 = <VerifiedPrefillOverlayContent locale={locale} stepUpKind={stepUpKind} isPending={vaultMutation.isPending} onSubmit={submitValue} />;
    $[11] = locale;
    $[12] = stepUpKind;
    $[13] = submitValue;
    $[14] = vaultMutation.isPending;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== handleClose || $[17] !== isExpanded || $[18] !== t6) {
    t7 = <BottomSheet open={isExpanded} onClose={handleClose} portalId={IDV_BODY_CONTENT_CONTAINER_ID} overlayClassName="!absolute !h-full !w-full">{t6}</BottomSheet>;
    $[16] = handleClose;
    $[17] = isExpanded;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
function _temp($_1) {
  return $_1.dob.label;
}
function _temp2($_0) {
  return $_0.ssn4.label;
}
function _temp3($) {
  return $.firstName.label;
}
function _temp4($_6) {
  return $_6.dob.title;
}
function _temp5($_7) {
  return $_7.dob.subtitle;
}
function _temp6($_4) {
  return $_4.ssn4.title;
}
function _temp7($_5) {
  return $_5.ssn4.subtitle;
}
function _temp8($_2) {
  return $_2.firstName.title;
}
function _temp9($_3) {
  return $_3.firstName.subtitle;
}
function _temp10($_8) {
  return $_8.cta;
}
function _temp11(position) {
  const container = document.getElementById(LEGACY_IDV_CONTAINER_ID);
  if (container) {
    container.scrollTo({
      top: position === "bottom" ? container.scrollHeight : 0,
      behavior: "instant"
    });
  }
}